import { darken, lighten } from '@mui/material'

import makePalette, { white } from './util'

/**
 * Miami Vice: linen, pastel and neon from 1984 Miami. Two themes share one
 * token table (the design system's `tokens.json`). Day sets ink-navy text on
 * linen and keeps the neons for fills; Night flips to the neons as text on
 * midnight navy. Each MUI role below is mapped from the token whose usage
 * note names that role, so the switch between the two is a pure value swap.
 */

/** Identity hues, the same in both themes. Fills, gradients, chips. */
const flamingo = '#ff6ec7'
const aqua = '#40e0d0'
const mango = '#ffc56e'
const palm = '#3ddc97'
/** Text and icons on any identity fill, in both themes. Never white on a neon. */
const onNeon = '#1e1b3a'

type MiamiViceTokens = {
  /** Page background. */
  canvas: string
  /** Cards, panels, table rows, inputs. */
  surface: string
  /** Menus, popovers, dialogs and hovered rows. */
  surfaceRaised: string
  /** Dividers and card borders. */
  hairline: string
  /** Headings and body copy. */
  ink: string
  /** Secondary text, labels, table headers. */
  ink2: string
  /** Captions, metadata, placeholders. */
  inkMuted: string
  /** Primary action fill, selected state, emphasis text. */
  accent: string
  /** Hover and pressed state of an accent fill. */
  accentHover: string
  /** Label and icon on an accent fill. */
  onAccent: string
  /** Links, secondary buttons, info. */
  link: string
  /** Hover state of links and of a link fill. */
  linkHover: string
  /** Label on a link fill. */
  onLink: string
  success: string
  warning: string
  danger: string
  /** Label on a success, warning or danger fill. */
  onStatus: string
}

const day: MiamiViceTokens = {
  canvas: '#fbf6ee',
  surface: '#fffcf7',
  surfaceRaised: '#ffffff',
  hairline: '#d8c9b8',
  ink: '#1e1b3a',
  ink2: '#4b4668',
  inkMuted: '#6e6a8a',
  accent: '#c21e6c',
  accentHover: '#a8185d',
  onAccent: white,
  link: '#0b6e72',
  linkHover: '#085457',
  onLink: white,
  success: '#157a4b',
  warning: '#8a5300',
  danger: '#b42318',
  onStatus: white
}

const night: MiamiViceTokens = {
  canvas: '#0d0f24',
  surface: '#171838',
  surfaceRaised: '#211f4c',
  hairline: '#332f66',
  ink: '#f2effa',
  ink2: '#b8b3d4',
  inkMuted: '#8e88b2',
  accent: flamingo,
  accentHover: '#ff8ad2',
  onAccent: '#160b22',
  link: aqua,
  linkHover: '#7cebe0',
  onLink: '#160b22',
  success: palm,
  warning: mango,
  danger: '#ff7a7a',
  onStatus: onNeon
}

/**
 * A status color has no hover token in the design system, so its hover tone
 * (MUI's `dark` slot) is derived from `main` the way the accent's is: Day
 * darkens, Night lightens. The `light` slot takes the matching identity hue
 * by day and the same lightened tone by night.
 */
const dayStatusColor = (main: string, light: string) => ({
  main,
  dark: darken(main, 0.2),
  light,
  contrastText: day.onStatus
})

const nightStatusColor = (main: string) => {
  const lifted = lighten(main, 0.2)
  return { main, dark: lifted, light: lifted, contrastText: night.onStatus }
}

/**
 * Day: pastels and ink on linen. The neons never carry text here; `accent`
 * and `link` are the same hues deepened to ink, and the identity hues sit in
 * the `light` slot for fills.
 */
export const miamiViceDay = {
  ...makePalette({
    action: {
      color: day.ink,
      opacities: {
        active: 0.54,
        hover: 0.04,
        selected: 0.08,
        disabled: 0.26,
        focus: 0.12
      }
    },

    palette: {
      mode: 'light',

      text: {
        primary: day.ink,
        secondary: day.ink2,
        disabled: day.inkMuted
      },

      background: {
        layout: day.canvas,
        default: day.canvas,
        paper: day.surface,
        navCard: day.surface
      },

      divider: day.hairline,

      customColors: {
        main: day.ink2,
        trackBg: day.hairline,
        tableHeaderBg: day.canvas
      }
    }
  }),

  primary: { main: day.accent, dark: day.accentHover, light: flamingo, contrastText: day.onAccent },
  secondary: { main: day.link, dark: day.linkHover, light: aqua, contrastText: day.onLink },
  info: { main: day.link, dark: day.linkHover, light: aqua, contrastText: day.onLink },
  success: dayStatusColor(day.success, palm),
  warning: dayStatusColor(day.warning, mango),
  error: dayStatusColor(day.danger, night.danger)
}

/**
 * Night: the neon version. The identity hues become the text colors on
 * midnight navy, and labels on a neon fill go dark plum, never white. Hover
 * lightens by night, so the `dark` slot (which MUI and `components.ts` use
 * for the hover tone) holds the lighter hover token.
 */
export const miamiViceNight = {
  ...makePalette({
    action: {
      color: night.ink,
      opacities: {
        active: 0.54,
        hover: 0.06,
        selected: 0.12,
        disabled: 0.3,
        focus: 0.12
      }
    },

    palette: {
      mode: 'dark',

      text: {
        primary: night.ink,
        secondary: night.ink2,
        disabled: night.inkMuted
      },

      background: {
        layout: night.canvas,
        default: night.canvas,
        paper: night.surface,
        navCard: night.surface
      },

      divider: night.hairline,

      customColors: {
        main: night.ink,
        trackBg: night.hairline,
        tableHeaderBg: night.surfaceRaised
      }
    }
  }),

  primary: { main: night.accent, dark: night.accentHover, light: night.accentHover, contrastText: night.onAccent },
  secondary: { main: night.link, dark: night.linkHover, light: night.linkHover, contrastText: night.onLink },
  info: { main: night.link, dark: night.linkHover, light: night.linkHover, contrastText: night.onLink },
  success: nightStatusColor(night.success),
  warning: nightStatusColor(night.warning),
  error: nightStatusColor(night.danger)
}
